import type { Rule } from "../types";
import { makeFiring } from "../util";

const EVIDENCE = "dijk_1995_deep_sleep_range";

/**
 * Bedtime drifted > 60 min from the user's target on ≥ 3 of the last 5 nights.
 * Sleep-pressure homeostasis (Dijk 1995) is sensitive to phase consistency;
 * irregular bedtimes erode SWS even when total time-in-bed is preserved.
 */
function timeToMinutes(hhmm: string | null): number | null {
  if (!hhmm) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

export const sleepWindowDrift: Rule = {
  id: "sleep_window_drift",
  domain: "health.recovery",
  topics: ["sleep", "circadian"],
  evidence_refs: [EVIDENCE],
  title: () => "Bedtime drifting from target",
  evaluate(ctx) {
    const sortedDesc = [...ctx.vitals].sort((a, b) => (a.date < b.date ? 1 : -1));
    const last5 = sortedDesc.slice(0, 5);
    if (last5.length < 5) return null;
    let drifts = 0;
    for (const row of last5) {
      const actual = timeToMinutes(row.bedtime_actual_local);
      const target = timeToMinutes(row.bedtime_target_local);
      if (actual == null || target == null) continue;
      // Wrap around midnight: shorter circular distance.
      let diff = Math.abs(actual - target);
      if (diff > 720) diff = 1440 - diff;
      if (diff > 60) drifts += 1;
    }
    if (drifts < 3) return null;
    return makeFiring({
      rule_id: "sleep_window_drift",
      now: ctx.now,
      tz: ctx.tz,
      severity: "warn",
      message: `Bedtime drifted >60 min from target on ${drifts} of the last 5 nights. Phase consistency feeds slow-wave sleep.`,
      evidence_refs: [EVIDENCE],
      factors: [
        {
          name: "drift_nights_count",
          value: drifts,
          contribution: "anchor bedtime",
          evidence_ref: EVIDENCE,
        },
      ],
      target_surface: "health.recovery",
    });
  },
};
